import { useState, useEffect } from 'react'
import { useAppStore } from '../store/useAppStore'
import { photoMemory } from '../store/photoMemoryStore'
import { apiService } from '../services/myrentcarApi'
import Card from '../components/ui/Card'
import { ArrowLeft, AlertTriangle, CheckCircle2, Loader2, AlertOctagon } from 'lucide-react'
import { usePendingMrcStore } from '../store/usePendingMrcStore'

// ── Types ──────────────────────────────────────────────────────────────────
interface ExistingEdl {
  completed_at?:  string
  agent_name?:    string
  mileage?:       number
  fuel?:          number
  mrc_pdf_id?:    string
  photo_urls?:    Record<string, string>
  damages?:       any[]
}

interface RemoteEdlState {
  depart: ExistingEdl | null
  retour: ExistingEdl | null
}

// ── Helpers ────────────────────────────────────────────────────────────────
function fmtDate(iso?: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return '' }
}

export default function ChooseScreen() {
  const { inspection, startInspection, initInspection, restoreFromDraft, setStep, credentials } = useAppStore()
  const contract = inspection?.contract
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState<string | null>(null)
  const [remoteEdl, setRemoteEdl]   = useState<RemoteEdlState | null>(null)
  const [checkingRemote, setCheckingRemote] = useState(true)

  const pendingReturns = usePendingMrcStore(s => s.pendingReturns)
  const isMrcPending = contract ? pendingReturns.some(r => String(r.id) === String(contract.rawId ?? contract.id)) : false

  // ── Au montage : vérifier si des EDL existent déjà en base ─────────────
  useEffect(() => {
    const rawId = contract?.rawId ?? contract?.id
    if (!rawId || !credentials) {
      setCheckingRemote(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const result = await apiService.getEDL(rawId, credentials)
        if (!cancelled) setRemoteEdl(result ?? { depart: null, retour: null })
      } catch (err) {
        console.warn('[ChooseScreen] Impossible de vérifier les EDL existants:', err)
        if (!cancelled) setRemoteEdl({ depart: null, retour: null })
      } finally {
        if (!cancelled) setCheckingRemote(false)
      }
    })()
    return () => { cancelled = true }
  }, [contract?.id, contract?.rawId, credentials])

  // ── Choix du type ──────────────────────────────────────────────────────
  const choose = async (type: 'depart' | 'retour') => {
    const rawId = contract?.rawId || contract?.id
    const existingRemote = remoteEdl?.[type]

    // ── 1. Vérification EDL finalisé EN BASE (multi-tablettes) ─────────
    if (existingRemote) {
      const photoCount = existingRemote.photo_urls
        ? Object.keys(existingRemote.photo_urls).length
        : 0
      const hasPdf = !!existingRemote.mrc_pdf_id
      const completedDate = fmtDate(existingRemote.completed_at)
      const agent = existingRemote.agent_name || 'un agent'

      const confirmOverwrite = window.confirm(
        `⚠️ ATTENTION — EDL ${type.toUpperCase()} DÉJÀ FINALISÉ ⚠️\n\n` +
        `Un état des lieux de ${type === 'depart' ? 'départ' : 'retour'} existe déjà :\n` +
        `• Réalisé le : ${completedDate}\n` +
        `• Agent : ${agent}\n` +
        `• Photos : ${photoCount}\n` +
        `• PDF MRC : ${hasPdf ? 'Oui ✅' : 'Non'}\n` +
        `• Km : ${existingRemote.mileage ?? '—'}\n\n` +
        `Voulez-vous créer un NOUVEL état des lieux ?\n` +
        `L'ancien sera ÉCRASÉ dans la base.\n\n` +
        `[OK] = Créer un NOUVEL EDL ${type.toUpperCase()}\n` +
        `[Annuler] = Revenir en arrière`
      )
      if (!confirmOverwrite) return
    }

    // ── 2. Vérification brouillon LOCAL (même tablette) ────────────────
    try {
      const { edlDraftStore } = await import('../store/useEdlDraftStore')
      if (rawId) {
        const draft = await edlDraftStore.get(rawId, type)
        if (draft) {
          const d = draft as any
          const hasSignatures = !!(d.signatureAgent || d.signatureClient)
          const isFinalized = !!(d.completedAt || hasSignatures)

          if (!isFinalized && !existingRemote) {
            // Simple brouillon local (et pas déjà averti par le check remote)
            const wantsResume = window.confirm(
              `📋 Brouillon ${type.toUpperCase()} en cours\n\n` +
              `Un brouillon non finalisé existe pour ce véhicule.\n` +
              `Étape : ${d.step || 'inconnue'}\n\n` +
              `[OK] = Reprendre le brouillon\n` +
              `[Annuler] = Recommencer à zéro`
            )

            if (wantsResume) {
              if (type === 'retour') {
                setLoading(true)
                try {
                  const { offlinePhotoStore } = await import('../store/offlinePhotoStore')
                  const cid = String(contract.rawId ?? contract.id)
                  const cached = await offlinePhotoStore.getDepartureCache(cid)
                  if (cached && typeof cached === 'object' && !Array.isArray(cached) && Object.keys(cached).length > 0) {
                    // ✅ FIX — Vérifier format cache
                    const firstVal = Object.values(cached)[0]
                    if (typeof firstVal === 'string' && firstVal.startsWith('http')) {
                      photoMemory.setDepartPhotosFromUrls(cached, cid)
                    } else {
                      console.warn('[ChooseScreen] Cache départ invalide, re-fetch...')
                      await offlinePhotoStore.clearDepartureCache(cid)
                      const result2 = await apiService.getDeparturePhotos(contract.id, credentials)
                      if (result2?.photos && typeof result2.photos === 'object' && !Array.isArray(result2.photos)
                          && Object.keys(result2.photos).length > 0) {
                        photoMemory.setDepartPhotosFromUrls(result2.photos, cid)
                        await offlinePhotoStore.saveDepartureCache(cid, result2.photos)
                      }
                    }
                  } else {
                    const result = await apiService.getDeparturePhotos(contract.id, credentials)
                    if (result?.photos && Object.keys(result.photos).length > 0) {
                      photoMemory.setDepartPhotosFromUrls(result.photos, cid)
                      await offlinePhotoStore.saveDepartureCache(cid, result.photos)
                    }
                  }
                } catch (err) {
                  console.warn('[ChooseScreen] Erreur chargement photos départ (brouillon):', err)
                } finally {
                  setLoading(false)
                }
              }
              restoreFromDraft(draft)
              return
            } else {
              const confirmOverwrite = window.confirm(
                `Voulez-vous ÉCRASER ce brouillon et recommencer ?\n\n` +
                `[OK] = Écraser et Recommencer\n` +
                `[Annuler] = Ne rien faire`
              )
              if (!confirmOverwrite) return
            }
          }
          // Si brouillon finalisé ou remote déjà averti → on écrase silencieusement
        }
      }
    } catch (e) {
      console.warn('Erreur chargement brouillon:', e)
    }

    // ── 3. Lancer l'inspection ──────────────────────────────────────────
    if (type === 'depart') {
      startInspection(contract, type)
    } else {
      if (contract) {
        initInspection(contract, type)
      }

      setLoading(true)
      setError(null)

      try {
        const { offlinePhotoStore } = await import('../store/offlinePhotoStore')
        const cid = String(contract.rawId ?? contract.id)
        const cached = await offlinePhotoStore.getDepartureCache(cid)

        if (cached && typeof cached === 'object' && !Array.isArray(cached) && Object.keys(cached).length > 0) {
          // ✅ FIX — Vérifier que le cache contient des URLs (string), pas des metadata MRC
          const firstValue = Object.values(cached)[0]
          if (typeof firstValue === 'string' && firstValue.startsWith('http')) {
            photoMemory.setDepartPhotosFromUrls(cached, cid)
            console.log('[ChooseScreen] Photos départ depuis cache:', Object.keys(cached).length)
          } else {
            // Cache corrompu (mauvais format) → vider et re-fetcher
            console.warn('[ChooseScreen] Cache départ invalide (format incorrect), re-fetch...')
            await offlinePhotoStore.clearDepartureCache(cid)
            const result = await apiService.getDeparturePhotos(contract.id, credentials)
            if (result?.photos && typeof result.photos === 'object' && !Array.isArray(result.photos)
                && Object.keys(result.photos).length > 0) {
              photoMemory.setDepartPhotosFromUrls(result.photos, cid)
              await offlinePhotoStore.saveDepartureCache(cid, result.photos)
              console.log('[ChooseScreen] Photos départ re-fetchées + cache corrigé:', Object.keys(result.photos).length)
            }
          }
        } else {
          const result = await apiService.getDeparturePhotos(contract.id, credentials)
          if (result?.photos && Object.keys(result.photos).length > 0) {
            photoMemory.setDepartPhotosFromUrls(result.photos, cid)
            await offlinePhotoStore.saveDepartureCache(cid, result.photos)
            console.log('[ChooseScreen] Photos départ depuis réseau + cache:', Object.keys(result.photos).length)
          } else {
            console.warn('[ChooseScreen] Aucune photo départ trouvée pour ce contrat')
          }
        }
      } catch (err) {
        console.error('[ChooseScreen] Erreur chargement photos départ:', err)
        setError('Photos départ non disponibles — EDL retour possible sans comparaison')
      } finally {
        setLoading(false)
        // ✅ FIX — naviguer APRÈS le chargement pour que les miniatures soient disponibles
        // Forcer le re-render du schéma en incrémentant _photoVersion
        useAppStore.setState((s: { _photoVersion?: number }) => ({ _photoVersion: (s._photoVersion ?? 0) + 1 }))
        setStep('recap')
      }
    }
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4 p-6">
        <p className="text-gray-500">Aucun contrat sélectionné</p>
        <button onClick={() => setStep('contracts')} className="text-blue-600 font-semibold">
          ← Retour aux contrats
        </button>
      </div>
    )
  }

  const departExists = remoteEdl?.depart
  const retourExists = remoteEdl?.retour

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Header */}
      <div className="bg-blue-600 px-4 pt-5 pb-4">
        <button
          onClick={() => setStep('contracts')}
          className="text-white/70 hover:text-white mb-3 flex items-center gap-1 text-sm"
        >
          <ArrowLeft size={16}/> Retour
        </button>
        <h1 className="text-white font-black text-xl">Type d'état des lieux</h1>
        <p className="text-blue-200 text-sm mt-1">
          {contract.plate} · {contract.vehicleName}
        </p>
        <p className="text-blue-100 text-sm">{contract.clientName}</p>
      </div>

      {/* Message d'erreur non bloquant */}
      {error && (
        <div className="mx-5 mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
          ⚠️ {error}
        </div>
      )}

      {/* Loader pendant la vérification Supabase */}
      {checkingRemote && (
        <div className="mx-5 mt-4 flex items-center justify-center gap-2 py-2">
          <Loader2 size={16} className="animate-spin text-blue-500"/>
          <span className="text-sm text-gray-500">Vérification des états existants…</span>
        </div>
      )}

      {/* Alerte Globale: si le retour est déjà fait mais que le contrat est encore actif dans MRC */}
      {(isMrcPending || retourExists) && (
        <div className="mx-4 mt-2 mb-4 bg-red-100 border-2 border-red-500 rounded-xl p-4 flex items-start gap-3 shadow-sm animate-pulse-slow">
          <AlertTriangle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-bold text-sm uppercase tracking-wide">
              Action requise dans MyRentCar
            </h3>
            <p className="text-red-700 text-sm mt-1 leading-snug">
              Le retour a été fait sur tablette, mais le contrat est <strong>toujours actif dans MyRentCar</strong>.
              <br/>Veuillez clôturer le dossier côté comptoir.
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 p-5 flex flex-col gap-4 justify-center max-w-lg mx-auto w-full">

        {/* Bouton DÉPART */}
        <button onClick={() => choose('depart')} className="w-full" disabled={loading || checkingRemote}>
          <Card className={`p-8 cursor-pointer hover:shadow-lg transition-all border-2 active:scale-[0.98] ${
            departExists
              ? 'border-orange-300 bg-orange-50/30 hover:border-orange-400'
              : 'hover:border-green-400'
          }`}>
            <div className="text-center">
              <div className="text-6xl mb-3">🚗</div>
              <h2 className="text-3xl font-black text-green-600 mb-2">DÉPART</h2>
              <p className="text-gray-500 text-sm">État des lieux de départ</p>

              {/* Badge EDL existant */}
              {departExists && (
                <div className="mt-3 bg-orange-100 border border-orange-300 rounded-xl px-3 py-2 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={14} className="text-orange-600 flex-shrink-0"/>
                    <span className="text-xs font-bold text-orange-700 uppercase">Départ déjà effectué</span>
                  </div>
                  <p className="text-xs text-orange-600">
                    {fmtDate(departExists.completed_at)}
                    {departExists.agent_name ? ` — ${departExists.agent_name}` : ''}
                  </p>
                  <p className="text-[10px] text-orange-500 mt-0.5">
                    Cliquer pour remplacer l'existant
                  </p>
                </div>
              )}
            </div>
          </Card>
        </button>

        {/* Bouton RETOUR */}
        <button onClick={() => choose('retour')} className="w-full" disabled={loading || checkingRemote}>
          <Card className={`p-8 cursor-pointer hover:shadow-lg transition-all border-2 active:scale-[0.98] ${
            retourExists
              ? 'border-orange-300 bg-orange-50/30 hover:border-orange-400'
              : 'hover:border-red-400'
          }`}>
            <div className="text-center">
              {loading
                ? <div className="text-5xl mb-3 animate-spin">⏳</div>
                : <div className="text-6xl mb-3">🏁</div>
              }
              <h2 className="text-3xl font-black text-red-600 mb-2">RETOUR</h2>
              <p className="text-gray-500 text-sm">
                {loading ? 'Chargement photos départ…' : 'État des lieux de retour'}
              </p>

              {/* Badge EDL existant */}
              {retourExists && (
                <div className="mt-3 bg-orange-100 border border-orange-300 rounded-xl px-3 py-2 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={14} className="text-orange-600 flex-shrink-0"/>
                    <span className="text-xs font-bold text-orange-700 uppercase">Retour déjà effectué</span>
                  </div>
                  <p className="text-xs text-orange-600">
                    {fmtDate(retourExists.completed_at)}
                    {retourExists.agent_name ? ` — ${retourExists.agent_name}` : ''}
                  </p>
                  <p className="text-[10px] text-orange-500 mt-0.5">
                    Cliquer pour remplacer l'existant
                  </p>
                </div>
              )}
            </div>
          </Card>
        </button>

        {/* Info départ existant = Retour possible */}
        {departExists && !retourExists && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
            <CheckCircle2 size={14} className="text-green-600 flex-shrink-0"/>
            <span className="text-xs text-green-700">
              Départ effectué — Les photos de départ seront chargées pour le retour
            </span>
          </div>
        )}

      </div>
    </div>
  )
}